import React, { FC, useEffect, useMemo } from "react";
import { createPlatePluginsOptions } from "./options";
import {
  createHistoryPlugin,
  createReactPlugin,
  setEventEditorId,
  Plate,
  useStoreEditorValue,
} from "@udecode/plate-core";
import {
  createBoldPlugin,
  createItalicPlugin,
  createUnderlinePlugin,
} from "@udecode/plate-basic-marks";
import { createHighlightPlugin } from "@udecode/plate-highlight";
import { createParagraphPlugin } from "@udecode/plate-paragraph";
import { createListPlugin } from "@udecode/plate-list";
import { createEditor, Descendant } from "slate";
import { EditableProps } from "slate-react/dist/components/editable";
import { createSlatePluginsComponents } from "./components";
import { ReactEditor, withReact } from "slate-react";

export type TextEditorProps = {
  id: string;
  initialValue: Descendant[];
  onBlur?: (value: Descendant[]) => void;
  autoCapitalize?: string;
  autoCorrect?: string;
  autoFocus?: boolean;
} & Pick<EditableProps, "className" | "style" | "placeholder">;
const COMPONENTS = createSlatePluginsComponents();
const DEFAULT_OPTIONS = createPlatePluginsOptions();
const PLUGINS = [
  // editor
  createReactPlugin(),
  createHistoryPlugin(),

  // marks
  createBoldPlugin(),
  createItalicPlugin(),
  createUnderlinePlugin(),
  createHighlightPlugin(),

  // elements
  createParagraphPlugin(),
  createListPlugin(),
];

export const TextEditor: FC<TextEditorProps> = ({
  id,
  className,
  style,
  placeholder,
  initialValue,
  onBlur,
  autoCorrect,
  autoCapitalize,
  autoFocus = false,
}) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  const value = useStoreEditorValue(id);
  const handleOnBlur = () => {
    setEventEditorId("focus", "");
    if (onBlur) {
      onBlur(value || []);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      autoFocus && setEventEditorId("focus", id);
      autoFocus && editor && ReactEditor.focus(editor);
    }, 100);
  }, [autoFocus, editor, id]);

  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <Plate
        id={id}
        initialValue={initialValue}
        components={COMPONENTS}
        options={DEFAULT_OPTIONS}
        plugins={PLUGINS}
        editor={editor}
        editableProps={{
          className,
          style,
          placeholder,
          onBlur: handleOnBlur,
          autoFocus,
          autoCapitalize: autoCapitalize ? autoCapitalize : "off",
          autoCorrect: autoCorrect ? autoCorrect : "off",
        }}
      />
    </div>
  );
};
